import { useState } from 'react';

import { ArrowDropUpTwoTone, CalendarTodayOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Tooltip,
  Typography
} from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import { useDiff } from '@/hooks/useDiff';
import { Creator } from '@/models/creator';
import { Updates } from '@/models/updates';
import { getRestrictionText } from '@/utils/creator';
import {
  formatVideoLength,
  getPrettyDateWithTime
} from '@/utils/datesAndTime/datesAndTime';

import NoUpdates from './NoUpdates';

interface ContentProps {
  creator?: string;
  updates?: Updates.Record[];
  uploadDate?: string;
  isExpired?: boolean;
}

type FieldValue =
  | string
  | number
  | boolean
  | string[]
  | Creator.Restriction[]
  | null
  | undefined;

interface ModalTextProps {
  fieldName: string;
  oldValue: FieldValue;
  newValue: FieldValue;
  type: 'old' | 'new';
  color?: string;
}

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  hashtags: 'Hashtags',
  authors: 'Collaborators',
  tags: 'Tags',
  platformDisclosure: 'Paid partnership',
  restrictions: 'Restrictions',
  location: 'Location',
  accessibilityCaption: 'Accessibility caption',
  videoLength: 'Video length'
};

const isEmpty = (value: FieldValue): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

const getVideoLengthDiff = (
  oldValue: FieldValue,
  newValue: FieldValue
): string => {
  if (isEmpty(oldValue) || isEmpty(newValue)) return '';

  const oldSeconds = Number(oldValue);
  const newSeconds = Number(newValue);

  if (Number.isNaN(oldSeconds) || Number.isNaN(newSeconds)) return '';
  if (oldSeconds < 0 || newSeconds < 0) return '';

  const diff = newSeconds - oldSeconds;
  const sign = diff >= 0 ? '+' : '-';
  return `${sign}${formatVideoLength(Math.abs(diff))}`;
};

const getSummaryText = ({ isDeleted, isReappeared }: Updates.Record) => {
  if (isDeleted) return 'The post was removed';
  if (isReappeared) return 'The post reappeared';
  return null;
};

const getEstimateText = (update: Updates.Record) => {
  if (!update.isDeleted && !update.isReappeared) return null;
  const label = update.isDeleted ? 'Deletion' : 'Reappearance';
  const dates = update.isDeleted ? update.deletedDates : update.reappearedDates;
  return `${label} estimate: ${getPrettyDateWithTime(
    dates?.firstDate
  )} - ${getPrettyDateWithTime(dates?.secondDate)}`;
};

const formatField = (fieldName: string, value: FieldValue): string => {
  if (isEmpty(value)) {
    return [
      'authors',
      'description',
      'title',
      'hashtags',
      'restrictions'
    ].includes(fieldName)
      ? 'None'
      : 'No';
  }

  if (fieldName === 'restrictions') {
    return (
      (value as Creator.Restriction[]).map(getRestrictionText).join('; ') ||
      'None'
    );
  }

  if (Array.isArray(value)) {
    const formatted = value
      .map((item) => (fieldName === 'hashtags' ? `#${item}` : `@${item}`))
      .join(fieldName === 'authors' ? ', ' : ' ');
    return formatted || 'None';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (fieldName === 'videoLength') {
    return formatVideoLength(Number(value));
  }

  return String(value);
};

const useStyles = makeStyles()((theme) => ({
  accordion: {
    boxShadow: 'none',
    '&.Mui-disabled': {
      backgroundColor: 'transparent'
    },
    '& .MuiAccordionSummary-root': {
      minHeight: 'unset',
      alignItems: 'flex-start',
      padding: 0,
      '&.Mui-disabled': {
        opacity: 1
      },
      '&.Mui-expanded': { minHeight: 'unset' },
      '& .MuiAccordionSummary-content': {
        margin: 0
      }
    },
    '& .MuiAccordionDetails-root': {
      padding: '8px 40px 0 0'
    }
  },
  newText: {
    color: theme.palette.success.main,
    fontWeight: 'bold'
  },
  oldText: {
    color: theme.palette.error.main,
    fontWeight: 'bold'
  }
}));

const DiffText = ({
  oldText,
  newText,
  type
}: {
  oldText: string;
  newText: string;
  type: 'old' | 'new';
}) => {
  const { classes } = useStyles();
  const diffParts = useDiff(oldText, newText, type);
  const highlightClass = type === 'old' ? classes.oldText : classes.newText;

  return (
    <>
      {diffParts.map((diff, i) => (
        <Box
          key={i}
          component="span"
          className={diff.highlight ? highlightClass : undefined}
        >
          {diff.text}
        </Box>
      ))}
    </>
  );
};

const ModalText = ({
  fieldName,
  oldValue,
  newValue,
  type,
  color
}: ModalTextProps) => {
  const formattedOld = formatField(fieldName, oldValue);
  const formattedNew = formatField(fieldName, newValue);
  const baseValue = type === 'old' ? formattedOld : formattedNew;

  const [expanded, setExpanded] = useState<boolean>(false);
  const shouldTruncate = baseValue.trim().split(/\s+/).length > 10;

  const NO_DIFF_FIELDS = ['restrictions', 'platformDisclosure'];
  const content = !NO_DIFF_FIELDS.includes(fieldName) ? (
    <DiffText oldText={formattedOld} newText={formattedNew} type={type} />
  ) : (
    baseValue
  );

  return (
    <Box
      maxWidth={500}
      whiteSpace={expanded || !shouldTruncate ? 'normal' : 'nowrap'}
      overflow={expanded || !shouldTruncate ? 'visible' : 'hidden'}
      textOverflow={expanded || !shouldTruncate ? 'clip' : 'ellipsis'}
      sx={{
        cursor: shouldTruncate ? 'pointer' : 'default',
        wordBreak: expanded ? 'break-all' : 'normal',
        overflowWrap: 'break-word'
      }}
      onClick={() => shouldTruncate && setExpanded(!expanded)}
    >
      <Typography variant="body2" color={color} component="span">
        {content}
      </Typography>
    </Box>
  );
};

export default function DialogContent({
  updates,
  creator,
  uploadDate,
  isExpired
}: ContentProps) {
  const { classes } = useStyles();
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  if (!updates || updates.length === 0 || isExpired) {
    return (
      <Box p={2}>
        <NoUpdates isExpired={isExpired} />
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box mb={3}>
        <Grid container>
          <Grid item xs={2}>
            <Box display="flex" gap={1}>
              <CalendarTodayOutlined sx={{ fontSize: 16 }} />
              <Typography variant="body2" fontWeight={500}>
                {getPrettyDateWithTime(uploadDate)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={10}>
            <Typography variant="body2">
              <Box component="span" fontWeight={600}>
                {creator}
              </Box>
              {' published the post'}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
      </Box>
      {updates.map((update, i) => (
        <Box key={i}>
          <Grid container>
            <Grid item xs={2}>
              <Box display="flex" gap={1}>
                {!update.isDeleted && (
                  <Box display="flex" gap={1}>
                    <CalendarTodayOutlined sx={{ fontSize: 16 }} />
                    <Typography variant="body2" fontWeight={500}>
                      {getPrettyDateWithTime(update.updatedAt)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={10}>
              <Accordion
                defaultExpanded
                disabled={update.isDeleted}
                className={classes.accordion}
              >
                <AccordionSummary
                  expandIcon={!update.isDeleted && <ArrowDropUpTwoTone />}
                >
                  <Typography variant="body2">
                    {getSummaryText(update) ?? (
                      <>
                        <Box component="span" fontWeight={600}>
                          {creator}
                        </Box>
                        {' changed the following:'}
                      </>
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {Object.entries(update.changes).map(
                    ([fieldName, change], changeIndex) => (
                      <Box my="6px" key={`${fieldName}-${changeIndex}`}>
                        <Typography
                          component="div"
                          display="flex"
                          variant="body2"
                          gap={1}
                          onMouseEnter={() =>
                            fieldName === 'videoLength' && setTooltipOpen(true)
                          }
                          onMouseLeave={() => setTooltipOpen(false)}
                        >
                          <Box component="span">{FIELD_LABELS[fieldName]}:</Box>
                          <ModalText
                            fieldName={fieldName}
                            oldValue={change.old}
                            newValue={change.new}
                            type="old"
                            color="text.secondary"
                          />
                          <Tooltip
                            open={fieldName === 'videoLength' && tooltipOpen}
                            title={getVideoLengthDiff(change.old, change.new)}
                            placement="bottom"
                            arrow
                          >
                            <Box sx={{ cursor: 'default' }} component="span">
                              →
                            </Box>
                          </Tooltip>
                          <ModalText
                            fieldName={fieldName}
                            oldValue={change.old}
                            newValue={change.new}
                            type="new"
                          />
                        </Typography>
                      </Box>
                    )
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                  >
                    {getEstimateText(update)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
        </Box>
      ))}
    </Box>
  );
}
